import React, { useCallback, useEffect, useState, type MutableRefObject } from 'react'

import { Input, InputNumber } from 'antd'

// Per-row 草稿。父層用 ref 收集，submit 時統一讀取——避免每打一字觸發父層 setState
// 重 render 整張 table（10K 筆時必卡）。
export interface ShelfItemDraft {
  counted_quantity: number | null
  note: string
}

export type ShelfItemDraftRef = MutableRefObject<Map<string, ShelfItemDraft>>

// 註冊每個編輯型 cell 的「外部寫入入口」，讓「填入帳面數量」按鈕（不同欄）可以呼叫
// 到同列 InputNumber 的 local setter。Key = inventory_id。
export type CellSetter = (value: number | null) => void
export type CellSettersRef = MutableRefObject<Map<string, CellSetter>>

interface CountedQuantityCellProps {
  inventoryId: string
  initialValue: number | null
  placeholder?: string
  draftRef: ShelfItemDraftRef
  settersRef: CellSettersRef
  onFilledChange: (inventoryId: string, isFilled: boolean) => void
}

function writeDraftCount(
  draftRef: ShelfItemDraftRef,
  inventoryId: string,
  value: number | null
) {
  const draft = draftRef.current.get(inventoryId) ?? { counted_quantity: null, note: '' }
  draft.counted_quantity = value
  draftRef.current.set(inventoryId, draft)
}

function writeDraftNote(
  draftRef: ShelfItemDraftRef,
  inventoryId: string,
  note: string
) {
  const draft = draftRef.current.get(inventoryId) ?? { counted_quantity: null, note: '' }
  draft.note = note
  draftRef.current.set(inventoryId, draft)
}

export const CountedQuantityCell = React.memo(function CountedQuantityCell({
  inventoryId,
  initialValue,
  placeholder,
  draftRef,
  settersRef,
  onFilledChange,
}: CountedQuantityCellProps) {
  const [value, setValue] = useState<number | null>(initialValue)

  const handleChange = useCallback((next: number | null) => {
    setValue(next)
    writeDraftCount(draftRef, inventoryId, next)
    onFilledChange(inventoryId, next !== null && next !== undefined)
  }, [draftRef, inventoryId, onFilledChange])

  // 註冊 setter，供 fill-reference 按鈕呼叫
  useEffect(() => {
    settersRef.current.set(inventoryId, handleChange)
    return () => {
      settersRef.current.delete(inventoryId)
    }
  }, [inventoryId, handleChange, settersRef])

  return (
    <InputNumber
      min={0}
      value={value}
      onChange={handleChange}
      placeholder={placeholder}
      style={{ width: 100 }}
    />
  )
})

interface NoteCellProps {
  inventoryId: string
  initialValue: string
  placeholder?: string
  draftRef: ShelfItemDraftRef
}

export const NoteCell = React.memo(function NoteCell({
  inventoryId,
  initialValue,
  placeholder,
  draftRef,
}: NoteCellProps) {
  const [value, setValue] = useState<string>(initialValue)

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value
    setValue(next)
    writeDraftNote(draftRef, inventoryId, next)
  }, [draftRef, inventoryId])

  return (
    <Input
      value={value}
      onChange={handleChange}
      placeholder={placeholder}
    />
  )
})
